import { AudienceFilterOptions, CoreFilterCommand, CustomFieldUsages, StickerAppliesTo, TagUsage, TrainingType, UnitTypeResponse, UserSettingsDto } from "generated/CoreApi_v1";
import { t } from "i18next";
import { AdvFilterStandardPicker, IAdvFilterCommand } from "views/general/AdvancedFilter";
import { IAdvFilterPickerProps } from "views/general/AdvancedFilter/interfaces";
import { AudienceOptionPicker } from "views/general/AdvancedFilter/Pickers/AudienceOptionPicker";
import { AudiencePicker } from "views/general/AdvancedFilter/Pickers/AudiencePicker";
import { ClassroomPicker } from "views/general/AdvancedFilter/Pickers/ClassroomPicker";
import { DaysPicker } from "views/general/AdvancedFilter/Pickers/DaysPicker";
import { EnrollmentHistoryPicker } from "views/general/AdvancedFilter/Pickers/EnrollmentHistoryPicker";
import { EnrollModePicker } from "views/general/AdvancedFilter/Pickers/EnrollModePicker";
import { EnrollmentStatusOptionPicker } from "views/general/AdvancedFilter/Pickers/EnrollStatusOptionPicker";
import { EnrollStatusPicker } from "views/general/AdvancedFilter/Pickers/EnrollStatusPicker";
import { ManagerPicker } from "views/general/AdvancedFilter/Pickers/ManagerPicker";
import { MemberPicker } from "views/general/AdvancedFilter/Pickers/MemberPicker";
import { PermissionPicker } from "views/general/AdvancedFilter/Pickers/PermissionPicker";
import { SecurityRolePicker } from "views/general/AdvancedFilter/Pickers/SecurityRolePicker";
import { TrainingContentTypePicker } from "views/general/AdvancedFilter/Pickers/TrainingContentTypePicker";
import { TrainingInstructorPicker } from "views/general/AdvancedFilter/Pickers/TrainingInstructorPicker";
import { TrainingWorkloadsPicker } from "views/general/AdvancedFilter/Pickers/TrainingWorkloadsPicker";
import { UsagePicker } from "views/general/AdvancedFilter/Pickers/UsagePicker";
import { UserBlockReasonPicker } from "views/general/AdvancedFilter/Pickers/UserBlockReasonPicker";

class FilterCmdUser {

  static tNS = { ns: "shared" };

  ///
  static managerDirectSubordinates = (group: number): IAdvFilterCommand => ({
    command: CoreFilterCommand.UserManagerDirectSubordinates,
    displayName: t("AdvFilter.Cmd.User.DirectSubordinates", this.tNS),
    canRepeat: true,
    group,
    CustomPicker: ManagerPicker,
  });

  ///
  static managerAllSubordinates = (group: number): IAdvFilterCommand => ({
    command: CoreFilterCommand.UserManagerAllSubordinates,
    displayName: t("AdvFilter.Cmd.User.AllSubordinates", this.tNS),
    canRepeat: true,
    group,
    CustomPicker: ManagerPicker,
  });

  ///
  static group = (group: number): IAdvFilterCommand => ({
    command: CoreFilterCommand.UserGroups,
    displayName: t("AdvFilter.Cmd.User.Group", this.tNS),
    canRepeat: true,
    group,
    picker: AdvFilterStandardPicker.Group,
  });

  ///
  static unitType = (unitTypes: UnitTypeResponse[] | undefined, group: number): IAdvFilterCommand[] => {

    var commands: IAdvFilterCommand[] = [];

    if (!!unitTypes) {
      unitTypes.map(x => commands.push({
        command: CoreFilterCommand.UserUnits,
        displayName: x.name,
        group: group,
        canRepeat: true,
        picker: AdvFilterStandardPicker.Unit,
        pickerParams: x
      }));
    }

    return commands;
  };

  ///
  static userSpecific = (group: number, customPicker?: React.FC<IAdvFilterPickerProps>, pickerParams?: string): IAdvFilterCommand => ({
    command: CoreFilterCommand.UserSpecific,
    displayName: t("AdvFilter.Cmd.User.SpecificUser", this.tNS),
    canRepeat: true,
    group,
    picker: customPicker ? undefined : AdvFilterStandardPicker.User,
    CustomPicker: customPicker,
    pickerParams: pickerParams,
  });

  ///
  static customField = (group: number): IAdvFilterCommand => ({
    command: CoreFilterCommand.UserCustomField,
    displayName: t("AdvFilter.Cmd.User.CustomField", this.tNS),
    canRepeat: true,
    group,
    picker: AdvFilterStandardPicker.CustomField,
    pickerParams: CustomFieldUsages.User
  });

  ///
  static onlyDeletedUsers = (group: number): IAdvFilterCommand => ({
    command: CoreFilterCommand.UsersOnlyDeleted,
    displayName: t("AdvFilter.Cmd.User.OnlyDeleted", this.tNS),
    canRepeat: false,
    group,
  });

  ///
  static onlyBlockedUsers = (group: number): IAdvFilterCommand => ({
    command: CoreFilterCommand.UserOnlyBlocked,
    displayName: t("AdvFilter.Cmd.User.OnlyBlocked", this.tNS),
    canRepeat: false,
    group,
  });

  ///
  static onlyUnblockedUsers = (group: number): IAdvFilterCommand => ({
    command: CoreFilterCommand.UserOnlyUnblocked,
    displayName: t("AdvFilter.Cmd.User.OnlyUnblocked", this.tNS),
    canRepeat: false,
    group,
  });

  ///
  static onlyInactives = (group: number): IAdvFilterCommand => ({
    command: CoreFilterCommand.UserOnlyInactive,
    displayName: t("AdvFilter.Cmd.User.OnlyInactive", this.tNS),
    canRepeat: false,
    group,
  });

  ///
  static includeBlockedUsers = (group: number): IAdvFilterCommand => ({
    command: CoreFilterCommand.UserIncludeBlockedUsers,
    displayName: t("AdvFilter.Cmd.User.IncludeBlockedUsers", this.tNS),
    canRepeat: false,
    group,
  });

  ///
  static includeInactive = (group: number): IAdvFilterCommand => ({
    command: CoreFilterCommand.UserIncludeInactive,
    displayName: t("AdvFilter.Cmd.User.IncludeInactive", this.tNS),
    canRepeat: false,
    group,
  });

  ///
  static includeDeleted = (group: number): IAdvFilterCommand => ({
    command: CoreFilterCommand.UserIncludeDeleted,
    displayName: t("AdvFilter.Cmd.User.IncludeDeleted", this.tNS),
    canRepeat: false,
    group,
  });

  ///
  static suspendedAccounts = (group: number): IAdvFilterCommand => ({
    command: CoreFilterCommand.UserSuspendedAccounts,
    displayName: t("AdvFilter.Cmd.User.SuspendedAccounts", this.tNS),
    canRepeat: false,
    group,
  });

  ///
  static isManager = (group: number): IAdvFilterCommand => ({
    command: CoreFilterCommand.UserIsManager,
    displayName: t("AdvFilter.Cmd.User.IsManager", this.tNS),
    canRepeat: true,
    group,
    picker: AdvFilterStandardPicker.Switch,
  });

  ///
  static isExternal = (group: number): IAdvFilterCommand => ({
    command: CoreFilterCommand.UserIsExternal,
    displayName: t("AdvFilter.Cmd.User.IsExternal", this.tNS),
    canRepeat: false,
    group,
    picker: AdvFilterStandardPicker.Switch,
  });

  ///
  static internals = (group: number): IAdvFilterCommand => ({
    command: CoreFilterCommand.UserInternal,
    displayName: t("AdvFilter.Cmd.User.Internal", this.tNS),
    canRepeat: false,
    group,
    optionGroup: "UserType"
  });

  ///
  static externals = (group: number): IAdvFilterCommand => ({
    command: CoreFilterCommand.UserExternal,
    displayName: t("AdvFilter.Cmd.User.External", this.tNS),
    canRepeat: false,
    group,
    optionGroup: "UserType"
  });

  ///
  static tag = (group: number): IAdvFilterCommand => ({
    command: CoreFilterCommand.UserTags,
    displayName: t("AdvFilter.Cmd.User.Tag", this.tNS),
    canRepeat: true,
    group,
    picker: AdvFilterStandardPicker.Tag,
    pickerParams: TagUsage.User,
  });

  ///
  static interestTag = (group: number): IAdvFilterCommand => ({
    command: CoreFilterCommand.UserInterestTag,
    displayName: t("AdvFilter.Cmd.User.InterestTag", this.tNS),
    canRepeat: true,
    group,
    picker: AdvFilterStandardPicker.Tag,
    pickerParams: TagUsage.UserInterest,
  });

  ///
  static topicOptional = (group: number): IAdvFilterCommand => ({
    command: CoreFilterCommand.UserTopicOptional,
    displayName: t("AdvFilter.Cmd.User.TopicOptional", this.tNS),
    canRepeat: true,
    group,
    picker: AdvFilterStandardPicker.Topic,
  });

  ///
  static topicRequired = (group: number): IAdvFilterCommand => ({
    command: CoreFilterCommand.UserTopicRequired,
    displayName: t("AdvFilter.Cmd.User.TopicRequired", this.tNS),
    canRepeat: true,
    group,
    picker: AdvFilterStandardPicker.Topic,
  });

  ///
  static hasSealProvenKnowledgeTopic = (group: number): IAdvFilterCommand => ({
    command: CoreFilterCommand.UserHasSealProvenKnowledgeTopic,
    displayName: t("AdvFilter.Cmd.User.HasSealProvenKnowledgeTopic", this.tNS),
    canRepeat: true,
    group,
    picker: AdvFilterStandardPicker.Topic,
  });

  ///
  static isSpecialistInTopic = (group: number): IAdvFilterCommand => ({
    command: CoreFilterCommand.UserIsSpecialistInTopic,
    displayName: t("AdvFilter.Cmd.User.IsSpecialistInTopic", this.tNS),
    canRepeat: true,
    group,
    picker: AdvFilterStandardPicker.Topic,
  });

  ///
  static workplace = (group: number, settings?: UserSettingsDto): IAdvFilterCommand => ({
    command: settings == undefined || settings.isWorkplaceFreeText == true ? CoreFilterCommand.UserWorkplaceText : CoreFilterCommand.UserWorkplaces,
    displayName: settings?.workplaceLabel ?? t("AdvFilter.Cmd.User.Workplace", this.tNS),
    canRepeat: true,
    group,
    picker: AdvFilterStandardPicker.Workplace,
    pickerParams: settings,
  });

  ///
  static channelMember = (group: number): IAdvFilterCommand => ({
    command: CoreFilterCommand.UserChannelMembers,
    displayName: t("AdvFilter.Cmd.User.ChannelMember", this.tNS),
    canRepeat: true,
    group,
    picker: AdvFilterStandardPicker.Channel,
  });

  ///
  static trainingAttending = (group: number): IAdvFilterCommand => ({
    command: CoreFilterCommand.UserTrainingAttending,
    displayName: t("AdvFilter.Cmd.User.TrainingAttending", this.tNS),
    canRepeat: true,
    group,
    picker: AdvFilterStandardPicker.Training,
  });

  ///
  static trainingApproved = (group: number): IAdvFilterCommand => ({
    command: CoreFilterCommand.UserTrainingApproved,
    displayName: t("AdvFilter.Cmd.User.TrainingApproved", this.tNS),
    canRepeat: true,
    group,
    picker: AdvFilterStandardPicker.Training,
  });

  ///
  static trainingReproved = (group: number): IAdvFilterCommand => ({
    command: CoreFilterCommand.UserTrainingReproved,
    displayName: t("AdvFilter.Cmd.User.TrainingReproved", this.tNS),
    canRepeat: true,
    group,
    picker: AdvFilterStandardPicker.Training,
  });

  ///
  static trainingAudience = (group: number): IAdvFilterCommand => ({
    command: CoreFilterCommand.UserTrainingAudience,
    displayName: t("AdvFilter.Cmd.User.TrainingAudience", this.tNS),
    canRepeat: false,
    group,
    picker: AdvFilterStandardPicker.Training,
  });

  ///
  static trainingMandatoryAudience = (group: number): IAdvFilterCommand => ({
    command: CoreFilterCommand.UserTrainingMandatoryAudience,
    displayName: t("AdvFilter.Cmd.User.TrainingMandatoryAudience", this.tNS),
    canRepeat: false,
    group,
    picker: AdvFilterStandardPicker.Training,
  });

  ///
  static blockReason = (group: number): IAdvFilterCommand => ({
    command: CoreFilterCommand.UserBlockReason,
    displayName: t("AdvFilter.Cmd.User.BlockReason", this.tNS),
    canRepeat: true,
    group,
    CustomPicker: UserBlockReasonPicker
  });

  ///
  static roles = (group: number): IAdvFilterCommand => ({
    command: CoreFilterCommand.UserRoles,
    displayName: t("AdvFilter.Cmd.User.Roles", this.tNS),
    canRepeat: true,
    group,
    CustomPicker: SecurityRolePicker
  });

  ///
  static permission = (group: number): IAdvFilterCommand => ({
    command: CoreFilterCommand.UserPermission,
    displayName: t("AdvFilter.Cmd.User.Permission", this.tNS),
    canRepeat: true,
    group,
    CustomPicker: PermissionPicker
  });

  ///
  static lastAccessBeforeOf = (group: number): IAdvFilterCommand => ({
    command: CoreFilterCommand.UserLastAccessBeforeOf,
    displayName: t("AdvFilter.Cmd.User.LastAccessBeforeOf", this.tNS),
    canRepeat: false,
    group,
    picker: AdvFilterStandardPicker.DateField
  });

  ///
  static lastAccessAfterOf = (group: number): IAdvFilterCommand => ({
    command: CoreFilterCommand.UserLastAccessAfterOf,
    displayName: t("AdvFilter.Cmd.User.LastAccessAfterOf", this.tNS),
    canRepeat: false,
    group,
    picker: AdvFilterStandardPicker.DateField
  });

  ///
  static hasActiveDevPlan = (group: number): IAdvFilterCommand => ({
    command: CoreFilterCommand.UserHasActiveDevPlan,
    displayName: t("AdvFilter.Cmd.User.DevPlanActive", this.tNS),
    canRepeat: false,
    group
  });

  ///
  static hasNotActiveDevPlan = (group: number): IAdvFilterCommand => ({
    command: CoreFilterCommand.UserHasNotActiveDevPlan,
    displayName: t("AdvFilter.Cmd.User.DevPlanNotActive", this.tNS),
    canRepeat: false,
    group,
    optionGroup: "devPlanStatus"
  });

  ///
  static hasDraftDevPlan = (group: number): IAdvFilterCommand => ({
    command: CoreFilterCommand.UserHasDraftDevPlan,
    displayName: t("AdvFilter.Cmd.User.DevPlanDraft", this.tNS),
    canRepeat: false,
    group,
    optionGroup: "devPlanStatus"
  });

  ///
  static hasDelayedDevPlan = (group: number): IAdvFilterCommand => ({
    command: CoreFilterCommand.UserHasDelayedDevPlan,
    displayName: t("AdvFilter.Cmd.User.DevPlanDelayed", this.tNS),
    canRepeat: false,
    group,
    optionGroup: "devPlanStatus"
  });

  ///
  static hasPendingApprovalDevPlan = (group: number): IAdvFilterCommand => ({
    command: CoreFilterCommand.UserHasPendingApprovalDevPlan,
    displayName: t("AdvFilter.Cmd.User.DevPlanPendingApproval", this.tNS),
    canRepeat: false,
    group,
    optionGroup: "devPlanStatus"
  });

}

class FilterCmdKnowledge {

  static tNS = { ns: "shared" };

  ///
  static channel = (group: number): IAdvFilterCommand => ({
    command: CoreFilterCommand.KnowledgeChannel,
    displayName: t("AdvFilter.Cmd.Knowledge.Channel", this.tNS),
    canRepeat: true,
    group,
    picker: AdvFilterStandardPicker.Channel,
  });

  ///
  static topic = (group: number, showDescendantOption?: Boolean): IAdvFilterCommand => ({
    command: CoreFilterCommand.KnowledgeTopic,
    displayName: t("AdvFilter.Cmd.Knowledge.Topic", this.tNS),
    displayPreText: t("AdvFilter.Cmd.Knowledge.Topic", this.tNS),
    canRepeat: true,
    group,
    picker: AdvFilterStandardPicker.TopicHierarchical,
    pickerParams: showDescendantOption ?? false
  });

  ///
  static sticker = (group: number): IAdvFilterCommand => ({
    command: CoreFilterCommand.KnowledgeSticker,
    displayName: t("AdvFilter.Cmd.Knowledge.Sticker", this.tNS),
    canRepeat: true,
    group,
    picker: AdvFilterStandardPicker.Sticker,
    pickerParams: StickerAppliesTo.Publication
  });

  ///
  static containVideo = (group: number): IAdvFilterCommand => ({
    command: CoreFilterCommand.KnowledgeContainVideo,
    displayName: t("AdvFilter.Cmd.Knowledge.ContainVideo", this.tNS),
    canRepeat: true,
    group,
  });

  ///
  static containAudio = (group: number): IAdvFilterCommand => ({
    command: CoreFilterCommand.KnowledgeContainAudio,
    displayName: t("AdvFilter.Cmd.Knowledge.ContainAudio", this.tNS),
    canRepeat: true,
    group,
  });

  ///
  static containImage = (group: number): IAdvFilterCommand => ({
    command: CoreFilterCommand.KnowledgeContainImage,
    displayName: t("AdvFilter.Cmd.Knowledge.ContainImage", this.tNS),
    canRepeat: true,
    group,
  });

  ///
  static containLink = (group: number): IAdvFilterCommand => ({
    command: CoreFilterCommand.KnowledgeContainLink,
    displayName: t("AdvFilter.Cmd.Knowledge.ContainLink", this.tNS),
    canRepeat: true,
    group,
  });

  ///
  static containDocument = (group: number): IAdvFilterCommand => ({
    command: CoreFilterCommand.KnowledgeContainDocument,
    displayName: t("AdvFilter.Cmd.Knowledge.ContainDocument", this.tNS),
    canRepeat: true,
    group,
  });

  ///
  static availableOnly = (group: number): IAdvFilterCommand => ({
    command: CoreFilterCommand.KnowledgeAvailableOnly,
    displayName: t("AdvFilter.Cmd.Knowledge.AvailableOnly", this.tNS),
    canRepeat: true,
    group,
  });

  ///
  static isInstitutional = (group: number): IAdvFilterCommand => ({
    command: CoreFilterCommand.KnowledgeInstitutional,
    displayName: t("AdvFilter.Cmd.Knowledge.Institutional", this.tNS),
    canRepeat: true,
    group,
  });

  ///
  static nonInstitutional = (group: number): IAdvFilterCommand => ({
    command: CoreFilterCommand.KnowledgeNonInstitutional,
    displayName: t("AdvFilter.Cmd.Knowledge.NonInstitutional", this.tNS),
    canRepeat: true,
    group,
  });

  ///
  static titleContains = (group: number): IAdvFilterCommand => ({
    command: CoreFilterCommand.KnowledgeTitleContains,
    displayName: t("AdvFilter.Cmd.Knowledge.ContainText", this.tNS),
    placeholder: t("AdvFilter.Cmd.Knowledge.ContainTextPlaceholder", this.tNS),
    canRepeat: true,
    group,
    picker: AdvFilterStandardPicker.TextField,
  });
}

class FilterCmdTraining {

  static tNS = { ns: "shared" };

  ///
  static specificTraining = (group: number, customPicker?: React.FC<IAdvFilterPickerProps>, pickerParams?: string): IAdvFilterCommand => ({
    command: CoreFilterCommand.TrainingSpecific,
    displayName: t("AdvFilter.Cmd.Training.TrainingSpecific", this.tNS),
    canRepeat: true,
    group,
    picker: customPicker ? undefined : AdvFilterStandardPicker.Training,
    pickerParams: pickerParams,
    CustomPicker: customPicker,
  });

  ///
  static currentUserInOptionalAudience = (group: number): IAdvFilterCommand => ({
    command: CoreFilterCommand.TrainingCurrentUserOptional,
    displayName: t("AdvFilter.Cmd.Training.UserOptional", this.tNS),
    canRepeat: true,
    group,
  });

  ///
  static currentUserInMandatoryAudience = (group: number): IAdvFilterCommand => ({
    command: CoreFilterCommand.TrainingCurrentUserMandatory,
    displayName: t("AdvFilter.Cmd.Training.UserMandatory", this.tNS),
    canRepeat: true,
    group,
  });

  ///
  static optionalAudienceContain = (group: number): IAdvFilterCommand => ({
    command: CoreFilterCommand.TrainingOptionalAudienceContain,
    displayName: t("AdvFilter.Cmd.Training.AudienceOptionalContain", this.tNS),
    canRepeat: false,
    group,
    CustomPicker: AudiencePicker
  });

  ///
  static requiredAudienceContain = (group: number): IAdvFilterCommand => ({
    command: CoreFilterCommand.TrainingRequiredAudienceContain,
    displayName: t("AdvFilter.Cmd.Training.AudienceRequiredContain", this.tNS),
    canRepeat: false,
    group,
    CustomPicker: AudiencePicker
  });

  ///
  static contentType = (group: number): IAdvFilterCommand => ({
    command: CoreFilterCommand.TrainingContentType,
    displayName: t("AdvFilter.Cmd.Training.ContentType", this.tNS),
    canRepeat: true,
    group,
    CustomPicker: TrainingContentTypePicker
  });

  ///
  static customField = (group: number): IAdvFilterCommand => ({
    command: CoreFilterCommand.TrainingCustomField,
    displayName: t("AdvFilter.Cmd.Training.CustomField", this.tNS),
    canRepeat: true,
    group,
    picker: AdvFilterStandardPicker.CustomField,
    pickerParams: CustomFieldUsages.CourseTraining
  });

  ///
  static coordinator = (group: number): IAdvFilterCommand => ({
    command: CoreFilterCommand.TrainingCoordinator,
    displayName: t("AdvFilter.Cmd.Training.Coordinator", this.tNS),
    canRepeat: true,
    group,
    CustomPicker: TrainingInstructorPicker,
    placeholder: t("AdvFilter.Picker.TrainingCoordinator.Placeholder", this.tNS)
  });

  ///
  static topic = (group: number, showDescendantOption?: Boolean): IAdvFilterCommand => ({
    command: CoreFilterCommand.TrainingTopic,
    displayName: t("AdvFilter.Cmd.Training.Topic", this.tNS),
    canRepeat: true,
    group,
    picker: AdvFilterStandardPicker.Topic,
    pickerParams: showDescendantOption ?? false
  });

  ///
  static instructor = (group: number): IAdvFilterCommand => ({
    command: CoreFilterCommand.TrainingInstructor,
    displayName: t("AdvFilter.Cmd.Training.Instructor", this.tNS),
    canRepeat: true,
    group,
    CustomPicker: TrainingInstructorPicker,
  });

  ///
  static language = (group: number): IAdvFilterCommand => ({
    command: CoreFilterCommand.TrainingLanguage,
    displayName: t("AdvFilter.Cmd.Training.Language", this.tNS),
    canRepeat: true,
    group,
    picker: AdvFilterStandardPicker.Language,
  });

  ///
  static inLearnPath = (group: number): IAdvFilterCommand => ({
    command: CoreFilterCommand.TrainingInLearnPath,
    displayName: t("AdvFilter.Cmd.Training.LearnPath", this.tNS),
    canRepeat: true,
    group,
    picker: AdvFilterStandardPicker.Training,
    pickerParams: TrainingType.LearningPath,
    placeholder: t("AdvFilter.Picker.LearnPath.Placeholder", this.tNS)
  });

  ///
  static sticker = (group: number): IAdvFilterCommand => ({
    command: CoreFilterCommand.TrainingSticker,
    displayName: t("AdvFilter.Cmd.Training.Sticker", this.tNS),
    canRepeat: true,
    group,
    picker: AdvFilterStandardPicker.Sticker,
    pickerParams: StickerAppliesTo.Training
  });

  ///
  static tag = (group: number): IAdvFilterCommand => ({
    command: CoreFilterCommand.TrainingTag,
    displayName: t("AdvFilter.Cmd.Training.Tag", this.tNS),
    canRepeat: true,
    group,
    picker: AdvFilterStandardPicker.Tag,
    pickerParams: TagUsage.Learning,
  });

  ///
  static publishPeriod = (group: number): IAdvFilterCommand => ({
    command: CoreFilterCommand.TrainingPublishPeriod,
    displayName: t("AdvFilter.Cmd.Training.PublishedPeriod", this.tNS),
    displayPreText: t("AdvFilter.Cmd.Training.PublishedPeriod", this.tNS),
    canRepeat: true,
    group,
    picker: AdvFilterStandardPicker.Period,
  });

  ///
  static workload = (group: number): IAdvFilterCommand => ({
    command: CoreFilterCommand.TrainingWorkload,
    displayName: t("AdvFilter.Cmd.Training.Workload", this.tNS),
    canRepeat: false,
    group,
    CustomPicker: TrainingWorkloadsPicker
  });

  ///
  static nameContain = (group: number): IAdvFilterCommand => ({
    command: CoreFilterCommand.TrainingNameContain,
    displayName: t("AdvFilter.Cmd.Training.NameContain", this.tNS),
    canRepeat: false,
    group,
    picker: AdvFilterStandardPicker.TextField
  });

  ///
  static nameOrExternalCodeContain = (group: number, placeholder?: string | undefined): IAdvFilterCommand => ({
    command: CoreFilterCommand.TrainingNameOrExternalCodeContain,
    displayName: t("AdvFilter.Cmd.Training.NameOrExternalCodeContain", this.tNS),
    canRepeat: false,
    group,
    picker: AdvFilterStandardPicker.TextField,
    placeholder: placeholder
  });

  ///
  static authorContain = (group: number): IAdvFilterCommand => ({
    command: CoreFilterCommand.TrainingAuthorContain,
    displayName: t("AdvFilter.Cmd.Training.AuthorContain", this.tNS),
    canRepeat: false,
    group,
    picker: AdvFilterStandardPicker.TextField
  });

  ///
  static externalCodeContain = (group: number): IAdvFilterCommand => ({
    command: CoreFilterCommand.TrainingExternalCodeContain,
    displayName: t("AdvFilter.Cmd.Training.ExternalCodeContain", this.tNS),
    canRepeat: false,
    group,
    picker: AdvFilterStandardPicker.TextField
  });

  ///
  static withUsersInProgress = (group: number): IAdvFilterCommand => ({
    command: CoreFilterCommand.TrainingWithUsersInProgress,
    displayName: t("AdvFilter.Cmd.Training.WithUserInProgress", this.tNS),
    canRepeat: false,
    group,
    picker: AdvFilterStandardPicker.Switch
  });

  ///
  static withAvaliableClassrooms = (group: number): IAdvFilterCommand => ({
    command: CoreFilterCommand.TrainingWithAvailableClassrooms,
    displayName: t("AdvFilter.Cmd.Training.WithAvailableClassrooms", this.tNS),
    canRepeat: true,
    group,
  });

  ///
  static withClasses = (group: number): IAdvFilterCommand => ({
    command: CoreFilterCommand.TrainingWithClasses,
    displayName: t("AdvFilter.Cmd.Training.WithClasses", this.tNS),
    canRepeat: false,
    group,
    picker: AdvFilterStandardPicker.Switch
  });

  ///
  static withUsersInterest = (group: number): IAdvFilterCommand => ({
    command: CoreFilterCommand.TrainingWithUsersInterest,
    displayName: t("AdvFilter.Cmd.Training.WithUsersInterest", this.tNS),
    canRepeat: false,
    group,
    picker: AdvFilterStandardPicker.Switch
  });

  ///
  static withMeeting = (group: number): IAdvFilterCommand => ({
    command: CoreFilterCommand.TrainingWithMeeting,
    displayName: t("AdvFilter.Cmd.Training.WithMeeting", this.tNS),
    canRepeat: true,
    group,
  });

  ///
  static createdOn = (group: number): IAdvFilterCommand => ({
    command: CoreFilterCommand.TrainingCreatedOn,
    displayName: t("AdvFilter.Cmd.Training.CreatedOn", this.tNS),
    displayPreText: t("AdvFilter.Cmd.Training.CreatedOn", this.tNS),
    canRepeat: false,
    group,
    picker: AdvFilterStandardPicker.Period,
  });

  ///
  static modifiedOn = (group: number): IAdvFilterCommand => ({
    command: CoreFilterCommand.TrainingModifiedOn,
    displayName: t("AdvFilter.Cmd.Training.ModifiedOn", this.tNS),
    displayPreText: t("AdvFilter.Cmd.Training.ModifiedOn", this.tNS),
    canRepeat: false,
    group,
    picker: AdvFilterStandardPicker.Period,
  });

  ///
  static isBuilding = (group: number): IAdvFilterCommand => ({
    command: CoreFilterCommand.TrainingIsBuilding,
    displayName: t("AdvFilter.Cmd.Training.IsBuilding", this.tNS),
    canRepeat: false,
    group,
    picker: AdvFilterStandardPicker.Switch,
  });

  ///
  static createdByYou = (group: number): IAdvFilterCommand => ({
    command: CoreFilterCommand.TrainingCreatedByYou,
    displayName: t("AdvFilter.Cmd.Training.CreatedByYou", this.tNS),
    canRepeat: false,
    group,
  });

  ///
  static createdByUser = (group: number): IAdvFilterCommand => ({
    command: CoreFilterCommand.TrainingCreatedByUser,
    displayName: t("AdvFilter.Cmd.Training.CreatedByUser", this.tNS),
    canRepeat: true,
    group,
    picker: AdvFilterStandardPicker.User,
  });

}

class FilterCmdClassroom {

  static tNS = { ns: "shared" };

  ///
  static withActiveUsers = (group: number): IAdvFilterCommand => ({
    command: CoreFilterCommand.ClassroomWithActiveUsers,
    displayName: t("AdvFilter.Cmd.Classroom.WithActiveUsers", this.tNS),
    canRepeat: true,
    group,
    picker: AdvFilterStandardPicker.Switch,
  });

  ///
  static executionPeriod = (group: number): IAdvFilterCommand => ({
    command: CoreFilterCommand.ClassroomExecutionPeriod,
    displayName: t("AdvFilter.Cmd.Classroom.ExecutionPeriod", this.tNS),
    displayPreText: t("AdvFilter.Cmd.Classroom.ExecutionPeriodPreText", this.tNS),
    canRepeat: false,
    group,
    picker: AdvFilterStandardPicker.Period,
  });

  ///
  static classroomNameContain = (group: number): IAdvFilterCommand => ({
    command: CoreFilterCommand.ClassroomNameContain,
    displayName: t("AdvFilter.Cmd.Classroom.ContainNameText", this.tNS),
    canRepeat: false,
    group,
    picker: AdvFilterStandardPicker.TextField,
  });

  ///
  static classroomExternalCodeContain = (group: number): IAdvFilterCommand => ({
    command: CoreFilterCommand.ClassroomExternalCodeContain,
    displayName: t("AdvFilter.Cmd.Classroom.ContainCodeText", this.tNS),
    canRepeat: false,
    group,
    picker: AdvFilterStandardPicker.TextField,
  });

  ///
  static classroomUsersInProgress = (group: number): IAdvFilterCommand => ({
    command: CoreFilterCommand.ClassroomUsersInProgress,
    displayName: t("AdvFilter.Cmd.Classroom.UserStudyingText", this.tNS),
    canRepeat: false,
    group,
    picker: AdvFilterStandardPicker.Switch,
  });

  ///
  static classroomWithOpenEnrollment = (group: number): IAdvFilterCommand => ({
    command: CoreFilterCommand.ClassroomWithOpenEnrollment,
    displayName: t("AdvFilter.Cmd.Classroom.EnrollmentOpenedText", this.tNS),
    canRepeat: false,
    group,
    picker: AdvFilterStandardPicker.Switch,
  });

  ///
  static classroomWithAudience = (group: number): IAdvFilterCommand => ({
    command: CoreFilterCommand.ClassroomWithAudience,
    displayName: t("AdvFilter.Cmd.Classroom.RestrictTargetAudienceText", this.tNS),
    canRepeat: false,
    group,
    picker: AdvFilterStandardPicker.Switch,
  });

  ///
  static classroomExpired = (group: number): IAdvFilterCommand => ({
    command: CoreFilterCommand.ClassroomExpired,
    displayName: t("AdvFilter.Cmd.Classroom.ExpiredText", this.tNS),
    canRepeat: false,
    group,
    picker: AdvFilterStandardPicker.Switch,
  });

  ///
  static classroomInstructor = (group: number): IAdvFilterCommand => ({
    command: CoreFilterCommand.ClassroomInstructor,
    displayName: t("AdvFilter.Cmd.Classroom.InstructorText", this.tNS),
    canRepeat: true,
    group,
    CustomPicker: TrainingInstructorPicker,
  });

  ///
  static classroomCreatedOn = (group: number): IAdvFilterCommand => ({
    command: CoreFilterCommand.ClassroomCreatedOn,
    displayName: t("AdvFilter.Cmd.Classroom.CreatedOnText", this.tNS),
    displayPreText: t("AdvFilter.Cmd.Classroom.CreatedOnText", this.tNS),
    canRepeat: false,
    group,
    picker: AdvFilterStandardPicker.Period,
  });

  ///
  static classroomModifiedOn = (group: number): IAdvFilterCommand => ({
    command: CoreFilterCommand.ClassroomModifiedOn,
    displayName: t("AdvFilter.Cmd.Classroom.ModifiedOnText", this.tNS),
    displayPreText: t("AdvFilter.Cmd.Classroom.ModifiedOnText", this.tNS),
    canRepeat: false,
    group,
    picker: AdvFilterStandardPicker.Period,
  });

  ///
  static classroomCreatedByYou = (group: number): IAdvFilterCommand => ({
    command: CoreFilterCommand.ClassroomCreatedByYou,
    displayName: t("AdvFilter.Cmd.Classroom.CreatedByYouText", this.tNS),
    canRepeat: false,
    group,
  });

  ///
  static classroomCreatedByUser = (group: number): IAdvFilterCommand => ({
    command: CoreFilterCommand.ClassroomCreatedByUser,
    displayName: t("AdvFilter.Cmd.Classroom.CreatedByText", this.tNS),
    canRepeat: false,
    group,
    picker: AdvFilterStandardPicker.User,
  });
}

class FilterCmdEnroll {

  static tNS = { ns: "shared" };

  ///
  static containsStatus = (group: number): IAdvFilterCommand => ({
    command: CoreFilterCommand.EnrollContainsStatus,
    displayName: t("AdvFilter.Cmd.Enroll.ContainsStatus", this.tNS),
    canRepeat: true,
    group,
    CustomPicker: EnrollStatusPicker,
  });

  ///
  static enrolledPeriod = (group: number): IAdvFilterCommand => ({
    command: CoreFilterCommand.EnrollPeriodEnrolled,
    displayName: t("AdvFilter.Cmd.Enroll.PeriodEnrolled", this.tNS),
    displayPreText: t("AdvFilter.Cmd.Enroll.PeriodEnrolledPreText", this.tNS),
    canRepeat: false,
    group,
    picker: AdvFilterStandardPicker.Period,
  });

  ///
  static conclusionPeriod = (group: number): IAdvFilterCommand => ({
    command: CoreFilterCommand.EnrollPeriodConclusion,
    displayName: t("AdvFilter.Cmd.Enroll.PeriodConclusion", this.tNS),
    displayPreText: t("AdvFilter.Cmd.Enroll.PeriodConclusionPreText", this.tNS),
    canRepeat: false,
    group,
    picker: AdvFilterStandardPicker.Period,
  });

  ///
  static lastAccessPeriod = (group: number): IAdvFilterCommand => ({
    command: CoreFilterCommand.EnrollPeriodLastAccess,
    displayName: t("AdvFilter.Cmd.Enroll.PeriodLastAccess", this.tNS),
    displayPreText: t("AdvFilter.Cmd.Enroll.PeriodLastAccess", this.tNS),
    canRepeat: false,
    group,
    picker: AdvFilterStandardPicker.Period,
  });

  ///
  static firstAccessPeriod = (group: number): IAdvFilterCommand => ({
    command: CoreFilterCommand.EnrollPeriodLastAccess,
    displayName: t("AdvFilter.Cmd.Enroll.PeriodFirstAccess", this.tNS),
    displayPreText: t("AdvFilter.Cmd.Enroll.PeriodFirstAccess", this.tNS),
    canRepeat: false,
    group,
    picker: AdvFilterStandardPicker.Period,
  });

  ///
  static historicPeriod = (group: number): IAdvFilterCommand => ({
    command: CoreFilterCommand.EnrollPeriodHistoric,
    displayName: t("AdvFilter.Cmd.Enroll.PeriodHistoric", this.tNS),
    displayPreText: t("AdvFilter.Cmd.Enroll.PeriodHistoric", this.tNS),
    canRepeat: false,
    group,
    picker: AdvFilterStandardPicker.Period,
  });

  ///
  static registrationMode = (group: number): IAdvFilterCommand => {
    return ({
      command: CoreFilterCommand.EnrollRegistrationMode,
      displayName: t("AdvFilter.Cmd.Enroll.RegistrationMode", this.tNS),
      canRepeat: true,
      group,
      CustomPicker: EnrollModePicker,
    });
  };

  ///
  static startedMoreThanDays = (group: number): IAdvFilterCommand => ({
    command: CoreFilterCommand.EnrollStartedMoreThanDays,
    displayName: t("AdvFilter.Cmd.Enroll.StartedMoreThanDays", this.tNS),
    canRepeat: false,
    group,
    CustomPicker: DaysPicker
  });

  ///
  static canceledOrRevoked = (group: number): IAdvFilterCommand => {
    return ({
      command: CoreFilterCommand.EnrollCanceledOrRevoked,
      displayName: t("AdvFilter.Cmd.Enroll.CanceledOrRevoked", this.tNS),
      canRepeat: false,
      group,
      CustomPicker: EnrollModePicker,
    });
  };

  ///
  static inProgress = (group: number): IAdvFilterCommand => ({
    command: CoreFilterCommand.EnrollInProgress,
    displayName: t("AdvFilter.Cmd.Enroll.InProgress", this.tNS),
    canRepeat: false,
    group,
    optionGroup: "enrollStatus",
  });

  ///
  static notStart = (group: number): IAdvFilterCommand => ({
    command: CoreFilterCommand.EnrollNotStart,
    displayName: t("AdvFilter.Cmd.Enroll.NotStarted", this.tNS),
    canRepeat: false,
    group,
    optionGroup: "enrollStatus",
  });

  ///
  static delayed = (group: number): IAdvFilterCommand => ({
    command: CoreFilterCommand.EnrollDelayed,
    displayName: t("AdvFilter.Cmd.Enroll.Delayed", this.tNS),
    canRepeat: false,
    group,
    optionGroup: "enrollStatus",
  });

  ///
  static completed = (group: number): IAdvFilterCommand => ({
    command: CoreFilterCommand.EnrollFinalized,
    displayName: t("AdvFilter.Cmd.Enroll.Completed", this.tNS),
    canRepeat: false,
    group,
    optionGroup: "enrollStatus",
  });

  ///
  static approved = (group: number): IAdvFilterCommand => ({
    command: CoreFilterCommand.EnrollApproved,
    displayName: t("AdvFilter.Cmd.Enroll.Approved", this.tNS),
    canRepeat: false,
    group,
    optionGroup: "enrollStatus",
  });

  ///
  static reproved = (group: number): IAdvFilterCommand => ({
    command: CoreFilterCommand.EnrollReproved,
    displayName: t("AdvFilter.Cmd.Enroll.Reproved", this.tNS),
    canRepeat: false,
    group,
    optionGroup: "enrollStatus",
  });

  ///
  static invalidated = (group: number): IAdvFilterCommand => ({
    command: CoreFilterCommand.EnrollInvalidated,
    displayName: t("AdvFilter.Cmd.Enroll.Invalidated", this.tNS),
    canRepeat: false,
    group,
  });

  ///
  static canceledAndRevoked = (group: number): IAdvFilterCommand => ({
    command: CoreFilterCommand.EnrollCanceledOrRevoked,
    displayName: t("AdvFilter.Cmd.Enroll.StatusCanceledOrRevoked", this.tNS),
    canRepeat: false,
    group,
    optionGroup: "enrollStatus",
  });

  ///
  static rejected = (group: number): IAdvFilterCommand => ({
    command: CoreFilterCommand.EnrollRejected,
    displayName: t("AdvFilter.Cmd.Enroll.Rejected", this.tNS),
    canRepeat: false,
    group,
    optionGroup: "enrollStatus",
  });

  ///
  static mandatoryAudience = (group: number, optionGroup?: string): IAdvFilterCommand => ({
    command: CoreFilterCommand.EnrollMandatoryAudience,
    displayName: t("AdvFilter.Cmd.Enroll.MandatoryAudience", this.tNS),
    canRepeat: false,
    group,
    optionGroup,
    canCheck: optionGroup != undefined
  });

  ///
  static enrollOptionalAudience = (group: number, optionGroup?: string): IAdvFilterCommand => ({
    command: CoreFilterCommand.EnrollOptionalAudience,
    displayName: t("AdvFilter.Cmd.Enroll.OptionalAudience", this.tNS),
    canRepeat: false,
    group,
    optionGroup,
    canCheck: optionGroup != undefined
  });

  ///
  static outOfAudience = (group: number, optionGroup?: string): IAdvFilterCommand => ({
    command: CoreFilterCommand.EnrollOutOfAudience,
    displayName: t("AdvFilter.Cmd.Enroll.OutOfAudience", this.tNS),
    canRepeat: false,
    group,
    optionGroup,
    canCheck: optionGroup != undefined
  });

  ///
  static userBlocked = (group: number): IAdvFilterCommand => ({
    command: CoreFilterCommand.EnrollBlockedUsersOnly,
    displayName: t("AdvFilter.Cmd.Enroll.BlockedUsersOnly", this.tNS),
    canRepeat: false,
    group,
  });

  ///
  static trainingMandatory = (group: number): IAdvFilterCommand => ({
    command: CoreFilterCommand.EnrollTrainingMandatory,
    displayName: t("AdvFilter.Cmd.Enroll.TrainingMandatory", this.tNS),
    canRepeat: false,
    group,
  });

  ///
  static certificationExpire = (group: number): IAdvFilterCommand => ({
    command: CoreFilterCommand.EnrollDoesCertificationExpire,
    displayName: t("AdvFilter.Cmd.Enroll.DoesCertificationExpire", this.tNS),
    canRepeat: false,
    group,
  });

  ///
  static certificationExpiringIn30Days = (group: number): IAdvFilterCommand => ({
    command: CoreFilterCommand.EnrollCertificationExpiringIn30Days,
    displayName: t("AdvFilter.Cmd.Enroll.CertificationExpiringIn30Days", this.tNS),
    canRepeat: false,
    group,
  });

  ///
  static certificationExpiringIn60Days = (group: number): IAdvFilterCommand => ({
    command: CoreFilterCommand.EnrollCertificationExpiringIn60Days,
    displayName: t("AdvFilter.Cmd.Enroll.CertificationExpiringIn60Days", this.tNS),
    canRepeat: false,
    group,
  });

  ///
  static hideInvalidated = (group: number): IAdvFilterCommand => ({
    command: CoreFilterCommand.EnrollHideInvalidated,
    displayName: t("AdvFilter.Cmd.Enroll.HideInvalidated", this.tNS),
    canRepeat: true,
    group,
  });

  ///
  static hideConcluded = (group: number): IAdvFilterCommand => ({
    command: CoreFilterCommand.EnrollHideConcluded,
    displayName: t("AdvFilter.Cmd.Enroll.HideConcluded", this.tNS),
    canRepeat: true,
    group,
  });
}

class FilterCmdEnrollOperation {

  static tNS = { ns: "shared" };

  static enrollOperationAudienceOption = (group: number): IAdvFilterCommand => ({
    command: CoreFilterCommand.EnrollOperationAudienceOption,
    displayName: t("AdvFilter.Cmd.Enroll.OperationAudienceOption", this.tNS),
    canRepeat: true,
    group,
    CustomPicker: AudienceOptionPicker
  });

  static enrollOperationAudienceMandatoryButtonOption = (group: number): IAdvFilterCommand => ({
    command: CoreFilterCommand.EnrollOperationAudienceOption,
    displayName: t("AdvFilter.Cmd.Enroll.OperationAudienceOption", this.tNS),
    fixedValue: AudienceFilterOptions.ContainsRequiredAudience,
    canRepeat: true,
    group,
    hideChip: true,
  });

  static enrollOperationAudienceMandatoryToEnrollButtonOption = (group: number): IAdvFilterCommand => ({
    command: CoreFilterCommand.EnrollOperationAudienceMandatoryToEnrollOption,
    displayName: t("AdvFilter.Cmd.Enroll.OperationAudienceOption", this.tNS),
    canRepeat: false,
    group,
    hideChip: true,
  });

  ///
  static enrollOperationClassroom = (group: number, trainingId: string, onlyActive: boolean): IAdvFilterCommand => ({
    command: CoreFilterCommand.EnrollOperationClassroom,
    displayName: t("AdvFilter.Cmd.Enroll.OperationClassroom", this.tNS),
    canRepeat: true,
    group,
    CustomPicker: ClassroomPicker,
    pickerParams: { trainingId, onlyActive }
  });

  ///
  static enrollOperationStatusEnrollment = (group: number): IAdvFilterCommand => ({
    command: CoreFilterCommand.EnrollOperationStatusEnrollment,
    displayName: t("AdvFilter.Cmd.Enroll.OperationStatusEnrollment", this.tNS),
    canRepeat: false,
    group,
    CustomPicker: EnrollmentStatusOptionPicker,
  });

  ///
  static operationEnrollmentHistory = (group: number): IAdvFilterCommand => ({
    command: CoreFilterCommand.EnrollOperationEnrollmentHistory,
    displayName: t("AdvFilter.Cmd.Enroll.OperationEnrollmentHistory", this.tNS),
    canRepeat: false,
    group,
    CustomPicker: EnrollmentHistoryPicker,
  });
}

class FilterDashboard {
  static tNS = { ns: "shared" };

  ///
  static usersInMandatoryAudience = (group: number): IAdvFilterCommand => ({
    command: CoreFilterCommand.DashboardUserMandatoryAudience,
    displayName: t("AdvFilter.Cmd.Dashboard.UserMandatoryAudience", this.tNS),
    canRepeat: false,
    group,
    hideChip: true
  });
}

class FilterAssessment {
  static tNS = { ns: "shared" };

  static topic = (group: number, showDescendantOption?: Boolean): IAdvFilterCommand => ({
    command: CoreFilterCommand.AssessmentTopic,
    displayName: t("AdvFilter.Cmd.Assessment.Topic", this.tNS),
    displayPreText: t("AdvFilter.Cmd.Assessment.Topic", this.tNS),
    canRepeat: true,
    group,
    picker: AdvFilterStandardPicker.TopicHierarchical,
    pickerParams: showDescendantOption ?? false
  });

  ///
  static language = (group: number): IAdvFilterCommand => ({
    command: CoreFilterCommand.AssessmentLanguage,
    displayName: t("AdvFilter.Cmd.Assessment.Language", this.tNS),
    canRepeat: true,
    group,
    picker: AdvFilterStandardPicker.Language,
  });

  ///
  static tag = (group: number): IAdvFilterCommand => ({
    command: CoreFilterCommand.AssessmentTag,
    displayName: t("AdvFilter.Cmd.Assessment.Tag", this.tNS),
    canRepeat: true,
    group,
    picker: AdvFilterStandardPicker.Tag,
    pickerParams: TagUsage.Assessment,
  });

  ///
  static nameContain = (group: number): IAdvFilterCommand => ({
    command: CoreFilterCommand.AssessmentNameContain,
    displayName: t("AdvFilter.Cmd.Assessment.NameContain", this.tNS),
    canRepeat: false,
    group,
    picker: AdvFilterStandardPicker.TextField
  });

  ///
  static authorContain = (group: number): IAdvFilterCommand => ({
    command: CoreFilterCommand.AssessmentAuthorContain,
    displayName: t("AdvFilter.Cmd.Assessment.AuthorContain", this.tNS),
    canRepeat: false,
    group,
    picker: AdvFilterStandardPicker.TextField
  });

  ///
  static externalCodeContain = (group: number): IAdvFilterCommand => ({
    command: CoreFilterCommand.AssessmentCodeContain,
    displayName: t("AdvFilter.Cmd.Assessment.ExternalCodeContain", this.tNS),
    canRepeat: false,
    group,
    picker: AdvFilterStandardPicker.TextField
  });

  ///
  static createdOn = (group: number): IAdvFilterCommand => ({
    command: CoreFilterCommand.AssessmentCreatedOn,
    displayName: t("AdvFilter.Cmd.Assessment.CreatedOn", this.tNS),
    displayPreText: t("AdvFilter.Cmd.Assessment.CreatedOn", this.tNS),
    canRepeat: false,
    group,
    picker: AdvFilterStandardPicker.Period,
  });

  ///
  static modifiedOn = (group: number): IAdvFilterCommand => ({
    command: CoreFilterCommand.AssessmentModifiedOn,
    displayName: t("AdvFilter.Cmd.Assessment.ModifiedOn", this.tNS),
    displayPreText: t("AdvFilter.Cmd.Assessment.ModifiedOn", this.tNS),
    canRepeat: false,
    group,
    picker: AdvFilterStandardPicker.Period,
  });

  ///
  static isBuilding = (group: number): IAdvFilterCommand => ({
    command: CoreFilterCommand.AssessmentIsBuilding,
    displayName: t("AdvFilter.Cmd.Assessment.IsBuilding", this.tNS),
    canRepeat: false,
    group,
    picker: AdvFilterStandardPicker.Switch,
  });

  ///
  static createdByYou = (group: number): IAdvFilterCommand => ({
    command: CoreFilterCommand.AssessmentCreatedByYou,
    displayName: t("AdvFilter.Cmd.Assessment.CreatedByYou", this.tNS),
    canRepeat: false,
    group,
  });

  ///
  static createdByUser = (group: number): IAdvFilterCommand => ({
    command: CoreFilterCommand.AssessmentCreatedByUser,
    displayName: t("AdvFilter.Cmd.Assessment.CreatedByUser", this.tNS),
    canRepeat: true,
    group,
    picker: AdvFilterStandardPicker.User,
  });

  ///
  static modifiedByUser = (group: number): IAdvFilterCommand => ({
    command: CoreFilterCommand.AssessmentModifiedByUser,
    displayName: t("AdvFilter.Cmd.Assessment.ModifiedByUser", this.tNS),
    canRepeat: true,
    group,
    picker: AdvFilterStandardPicker.User,
  });
}

class FilterCmdGroup {
  static tNS = { ns: "shared" };

  ///
  static containsMember = (group: number): IAdvFilterCommand => ({
    command: CoreFilterCommand.GroupContainsMember,
    displayName: t("AdvFilter.Cmd.Group.ContainsMembers", this.tNS),
    canRepeat: true,
    group,
    CustomPicker: MemberPicker
  });

  ///
  static tag = (group: number): IAdvFilterCommand => ({
    command: CoreFilterCommand.GroupTags,
    displayName: t("AdvFilter.Cmd.General.Tag", this.tNS),
    canRepeat: true,
    group,
    picker: AdvFilterStandardPicker.Tag,
    pickerParams: TagUsage.Group,
  });

  ///
  static usage = (group: number): IAdvFilterCommand => ({
    command: CoreFilterCommand.GroupUsage,
    displayName: t("AdvFilter.Cmd.Group.Usage", this.tNS),
    canRepeat: false,
    group,
    CustomPicker: UsagePicker
  });

  ///
  static unit = (group: number): IAdvFilterCommand => ({
    command: CoreFilterCommand.GroupUnit,
    displayName: t("AdvFilter.Cmd.General.Unit", this.tNS),
    canRepeat: true,
    group,
    picker: AdvFilterStandardPicker.Unit,
  });

  ///
  static activeAndInactive = (group: number): IAdvFilterCommand => ({
    command: CoreFilterCommand.GroupActiveAndInactive,
    displayName: t("AdvFilter.Cmd.General.ActiveAndInactive", this.tNS),
    canRepeat: false,
    group,
    optionGroup: "status"
  });

  ///
  static inactiveOnly = (group: number): IAdvFilterCommand => ({
    command: CoreFilterCommand.GroupInactiveOnly,
    displayName: t("AdvFilter.Cmd.General.OnlyInactive", this.tNS),
    canRepeat: false,
    group,
    optionGroup: "status"
  });

  ///
  static suspendedOnly = (group: number): IAdvFilterCommand => ({
    command: CoreFilterCommand.GroupSuspendedOnly,
    displayName: t("AdvFilter.Cmd.Group.SuspendedUpdate", this.tNS),
    canRepeat: false,
    group,
  });
}

class FilterCmdUnit {
  static tNS = { ns: "shared" };

  ///
  static user = (group: number): IAdvFilterCommand => ({
    command: CoreFilterCommand.UnitUser,
    displayName: t("AdvFilter.Cmd.General.User", this.tNS),
    canRepeat: true,
    group,
    picker: AdvFilterStandardPicker.User,
  });

  ///
  static tag = (group: number): IAdvFilterCommand => ({
    command: CoreFilterCommand.UnitTag,
    displayName: t("AdvFilter.Cmd.General.Tag", this.tNS),
    canRepeat: true,
    group,
    picker: AdvFilterStandardPicker.Tag,
    pickerParams: TagUsage.Unit,
  });

  ///
  static customField = (group: number): IAdvFilterCommand => ({
    command: CoreFilterCommand.UserCustomField,
    displayName: t("AdvFilter.Cmd.General.CustomField", this.tNS),
    canRepeat: true,
    group,
    picker: AdvFilterStandardPicker.CustomField,
    pickerParams: CustomFieldUsages.Unit
  });
}

class FilterCmdTopic {
  static tNS = { ns: "shared" };

  ///
  static subtopic = (group: number): IAdvFilterCommand => ({
    command: CoreFilterCommand.TopicSubTopic,
    displayName: t("AdvFilter.Cmd.Topic.Subtopic", this.tNS),
    canRepeat: true,
    group,
    picker: AdvFilterStandardPicker.Topic,
  });

  ///
  static tag = (group: number): IAdvFilterCommand => ({
    command: CoreFilterCommand.TopicTags,
    displayName: t("AdvFilter.Cmd.General.Tag", this.tNS),
    canRepeat: true,
    group,
    picker: AdvFilterStandardPicker.Tag,
    pickerParams: TagUsage.Topic,
  });

  ///
  static knowledge = (group: number): IAdvFilterCommand => ({
    command: CoreFilterCommand.TopicKnowledge,
    displayName: t("AdvFilter.Cmd.Topic.Knowledge", this.tNS),
    canRepeat: false,
    group,
  });

  ///
  static skill = (group: number): IAdvFilterCommand => ({
    command: CoreFilterCommand.TopicSkill,
    displayName: t("AdvFilter.Cmd.Topic.Skill", this.tNS),
    canRepeat: false,
    group,
  });

  ///
  static competency = (group: number): IAdvFilterCommand => ({
    command: CoreFilterCommand.TopicCompetency,
    displayName: t("AdvFilter.Cmd.Topic.Competency", this.tNS),
    canRepeat: false,
    group,
  });

  ///
  static bundler = (group: number): IAdvFilterCommand => ({
    command: CoreFilterCommand.TopicBundler,
    displayName: t("AdvFilter.Cmd.Topic.Bundler", this.tNS),
    canRepeat: false,
    group,
  });

  ///
  static withPublications = (group: number): IAdvFilterCommand => ({
    command: CoreFilterCommand.TopicWithPublications,
    displayName: t("AdvFilter.Cmd.Topic.WithPublications", this.tNS),
    canRepeat: false,
    group,
    optionGroup: ""
  });

  ///
  static withoutPublications = (group: number): IAdvFilterCommand => ({
    command: CoreFilterCommand.TopicWithoutPublications,
    displayName: t("AdvFilter.Cmd.Topic.WithoutPublications", this.tNS),
    canRepeat: false,
    group,
    optionGroup: ""
  });

  ///
  static withChallenge = (group: number): IAdvFilterCommand => ({
    command: CoreFilterCommand.TopicWithChallenge,
    displayName: t("AdvFilter.Cmd.Topic.WithChallenge", this.tNS),
    canRepeat: false,
    group,
    optionGroup: ""
  });

  ///
  static withoutChallenge = (group: number): IAdvFilterCommand => ({
    command: CoreFilterCommand.TopicWithoutChallenge,
    displayName: t("AdvFilter.Cmd.Topic.WithoutChallenge", this.tNS),
    canRepeat: false,
    group,
    optionGroup: ""
  });

  ///
  static withTraining = (group: number): IAdvFilterCommand => ({
    command: CoreFilterCommand.TopicWithTraining,
    displayName: t("AdvFilter.Cmd.Topic.WithTraining", this.tNS),
    canRepeat: false,
    group,
    optionGroup: ""
  });

  ///
  static withoutTraining = (group: number): IAdvFilterCommand => ({
    command: CoreFilterCommand.TopicWithoutTraining,
    displayName: t("AdvFilter.Cmd.Topic.WithoutTraining", this.tNS),
    canRepeat: false,
    group,
    optionGroup: ""
  });

  ///
  static withSpecialist = (group: number): IAdvFilterCommand => ({
    command: CoreFilterCommand.TopicWithSpecialist,
    displayName: t("AdvFilter.Cmd.Topic.WithSpecialist", this.tNS),
    canRepeat: false,
    group,
    optionGroup: ""
  });

  ///
  static withoutSpecialist = (group: number): IAdvFilterCommand => ({
    command: CoreFilterCommand.TopicWithoutSpecialist,
    displayName: t("AdvFilter.Cmd.Topic.WithoutSpecialist", this.tNS),
    canRepeat: false,
    group,
    optionGroup: ""
  });

  ///
  static activeAndInactive = (group: number): IAdvFilterCommand => ({
    command: CoreFilterCommand.TopicActiveAndInactive,
    displayName: t("AdvFilter.Cmd.General.ActiveAndInactive", this.tNS),
    canRepeat: false,
    group,
    optionGroup: "status"
  });

  ///
  static inactiveOnly = (group: number): IAdvFilterCommand => ({
    command: CoreFilterCommand.TopicInactiveOnly,
    displayName: t("AdvFilter.Cmd.General.OnlyInactive", this.tNS),
    canRepeat: false,
    group,
    optionGroup: "status"
  });
}

class FilterCmdRole {
  static tNS = { ns: "shared" };

  ///
  static permission = (group: number): IAdvFilterCommand => ({
    command: CoreFilterCommand.RoleContainPermision,
    displayName: t("AdvFilter.Cmd.Role.ContainsPermission", this.tNS),
    canRepeat: true,
    group,
    CustomPicker: PermissionPicker
  });

  ///
  static user = (group: number): IAdvFilterCommand => ({
    command: CoreFilterCommand.RoleAssignedUser,
    displayName: t("AdvFilter.Cmd.Role.User", this.tNS),
    canRepeat: true,
    group,
    picker: AdvFilterStandardPicker.User,
  });
}

class FilterCmdBanner {

  static tNS = { ns: "shared" };

  static withAudience = (group: number): IAdvFilterCommand => ({
    command: CoreFilterCommand.BannerWithAudience,
    displayName: t("AdvFilter.Cmd.Banner.WithAudience", this.tNS),
    canRepeat: false,
    group: 2,
    optionGroup: "BannerAudience"
  });

  static notAudience = (group: number): IAdvFilterCommand => ({
    command: CoreFilterCommand.BannerNotAudience,
    displayName: t("AdvFilter.Cmd.Banner.NotAudience", this.tNS),
    canRepeat: false,
    group: 2,
    optionGroup: "BannerAudience"
  });

  static forUser = (group: number): IAdvFilterCommand => ({
    command: CoreFilterCommand.BannerForUser,
    displayName: t("AdvFilter.Cmd.Banner.ForUser", this.tNS),
    canRepeat: false,
    group: 2,
    picker: AdvFilterStandardPicker.User,
  });
}

class FilterGeneral {
  static tNS = { ns: "shared" };

  ///
  static combineUsingOR = (group: number): IAdvFilterCommand => ({
    command: CoreFilterCommand.GeneralCombineUsingOR,
    displayName: t("AdvFilter.Cmd.General.CombineUsingOR", this.tNS),
    canRepeat: false,
    group,
  });
}

export class FilterCmd {

  static User = FilterCmdUser;

  static Group = FilterCmdGroup;

  static Unit = FilterCmdUnit;

  static Topic = FilterCmdTopic;

  static Role = FilterCmdRole;

  static Banner = FilterCmdBanner;

  static Knowledge = FilterCmdKnowledge;

  static Training = FilterCmdTraining;

  static Classroom = FilterCmdClassroom;

  static Enroll = FilterCmdEnroll;

  static EnrollOperation = FilterCmdEnrollOperation;

  static Dashboard = FilterDashboard;

  static Assessment = FilterAssessment;

  static General = FilterGeneral;
}
